/**
 * The monthly HMIS report (NCD), calculated on the device so it can be copied onto the paper
 * form without a connection; an administrator can send it to DHIS2 through the server.
 */
import {
  ETH_MONTHS_EN,
  type HmisReport,
  type PatientBundle,
  computeHmisReport,
  hmisPeriod,
  lastCompletePeriod,
} from '@assist/core';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { loadBundles } from '../care.js';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';

interface PushReply {
  ok?: boolean;
  dryRun?: boolean;
  imported?: number;
  updated?: number;
  ignored?: number;
  conflicts?: Array<{ object?: string; value?: string }>;
  unmapped?: string[];
}

/** A breakdown key as words: the platform's labels, else the pack's own choice label ("svd" ->
 *  "SVD — spontaneous vaginal"), else the code. */
const labelWith = (choices: Map<string, string>) => (part: string) => {
  const [k, v] = part.split('=');
  if (!v) return t.keyLabels[k ?? ''] ?? choices.get(k ?? '') ?? k ?? '';
  return k === 'age' ? v : (t.keyLabels[v] ?? choices.get(v) ?? v);
};

export function Hmis() {
  const a = useApp();
  const last = lastCompletePeriod(clock.today());
  const [year, setYear] = useState(last.year);
  const [month, setMonth] = useState(last.month);
  const [bundles, setBundles] = useState<PatientBundle[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<PushReply | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the store changes
  useEffect(() => {
    let live = true;
    void loadBundles(true).then((b) => live && setBundles(b));
    return () => {
      live = false;
    };
  }, [a.status]);
  const packs = activePacks(a.packSettings)
    .map((x) => x.pack)
    .filter((p) => p.hmis);
  const choiceLabels = new Map<string, string>();
  for (const p of packs)
    for (const c of p.concepts)
      for (const ch of c.choices ?? [])
        if (!choiceLabels.has(ch.code)) choiceLabels.set(ch.code, ch.display);
  const period = useMemo(
    () => hmisPeriod(year, month, packs[0]?.hmis?.cutoff_day ?? 20),
    [year, month, packs],
  );
  const report: HmisReport | null = useMemo(
    () =>
      bundles
        ? {
            period,
            elements: packs.flatMap((p) =>
              p.hmis ? computeHmisReport(p.hmis, bundles, period, p.measures).elements : [],
            ),
          }
        : null,
    [bundles, period, packs],
  );

  const send = async () => {
    setBusy(true);
    setErr(null);
    setReply(null);
    try {
      setReply(
        await app.api<PushReply>('/api/admin/hmis/push', {
          method: 'POST',
          body: JSON.stringify({ period: period.id }),
        }),
      );
    } catch (e) {
      setErr(a.online ? (e instanceof Error ? e.message : String(e)) : t.needsOnline);
    } finally {
      setBusy(false);
    }
  };

  const years = [last.year - 2, last.year - 1, last.year];
  return (
    <section>
      <div class="card">
        <h3>{t.hmisTitle}</h3>
        <div class="period-pick">
          <label>
            {t.month}{' '}
            <select value={String(month)} onChange={(e) => setMonth(Number(e.currentTarget.value))}>
              {ETH_MONTHS_EN.slice(0, 12).map((m, i) => (
                <option key={m} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.year}{' '}
            <select value={String(year)} onChange={(e) => setYear(Number(e.currentTarget.value))}>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y} E.C.
                </option>
              ))}
            </select>
          </label>
        </div>
        <p class="muted">{t.hmisHelp(eth(period.start), eth(period.end))}</p>
        {!packs.length ? <p class="muted">{t.noPacks}</p> : null}
        {!report ? <p class="muted">{t.computing}</p> : null}
      </div>
      {report?.elements.map((el) => {
        const label = labelWith(choiceLabels);
        const def = packs.flatMap((p) => p.hmis?.elements ?? []).find((d) => d.code === el.code);
        const groups = new Map<string, Array<{ key: string; value: number }>>();
        for (const v of el.values) {
          if (v.key === 'total' || v.key === 'registered') continue;
          const g = v.key
            .split(',')
            .map((p) => p.split('=')[0])
            .join(',');
          groups.set(g, [...(groups.get(g) ?? []), v]);
        }
        return (
          <div class="card" key={el.code} data-hmis={el.code}>
            <h3>
              {el.code} <span class="muted">· {el.title}</span>
            </h3>
            <p>
              <strong>
                {el.values.find((v) => v.key === 'registered')
                  ? t.keyLabels.registered
                  : t.keyLabels.total}
                : {el.total}
              </strong>
            </p>
            <div class="hmisgrid">
              {[...groups.entries()].map(([g, vals]) => (
                <table class="hmis" key={g}>
                  <caption>{t.groupLabels[g] ?? g}</caption>
                  <tbody>
                    {vals.map((v) => (
                      <tr key={v.key}>
                        <th scope="row">{v.key.split(',').map(label).join(' · ')}</th>
                        <td>{v.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
            {el.outsideAgeBands ? <p class="help">{t.outsideBands(el.outsideAgeBands)}</p> : null}
            {def ? (
              <details>
                <summary>{t.why}</summary>
                <p class="help">{def.description}</p>
                <p class="help">
                  {t.source}: {def.sources.map((s) => `${s.source}, ${s.locator}`).join('; ')}
                </p>
              </details>
            ) : null}
          </div>
        );
      })}
      {a.session?.user.role === 'admin' && packs.length ? (
        <div class="card">
          <p class="muted">{t.dhis2ServerNote}</p>
          <button type="button" class="primary" disabled={busy} onClick={() => void send()}>
            {busy ? t.sending : t.sendDhis2}
          </button>
          {err ? <div class="alert">{err}</div> : null}
          {reply ? (
            <div class={reply.ok ? 'ok-note' : 'alert'}>
              {reply.dryRun ? <p>{t.dhis2DryRun}</p> : null}
              <p>{t.dhis2Sent(reply.imported ?? 0, reply.updated ?? 0, reply.ignored ?? 0)}</p>
              {reply.unmapped?.length ? <p>{t.dhis2Unmapped(reply.unmapped.length)}</p> : null}
              {reply.conflicts?.length ? (
                <ul>
                  {reply.conflicts.map((c) => (
                    <li key={`${c.object}${c.value}`}>
                      {c.object}: {c.value}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
